import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { UsageEvent, UsageEventType, UsageTrackingConfig } from '../models/usage-event';
import { buildInteractionDescriptor, resolveInteractionTarget } from '../helpers/usage-interaction';

interface StoredQueue {
  events: UsageEvent[];
  droppedCount: number;
}

const STORAGE_KEY = 'navUsageQueue';
const PERSIST_DEBOUNCE_MS = 1000;

/** crypto.randomUUID is undefined outside secure contexts (e.g. plain-http LAN access), so fall back to Math.random v4. */
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random % 4) + 8;
    return value.toString(16);
  });
}

/**
 * First-party usage tracking. Buffers events, persists the queue to
 * localStorage (the durability guarantee across tab close), and flushes
 * batches to the Navigator API which forwards to Firehose → Redshift.
 *
 * Inert until the host app calls init(); every entry point no-ops when
 * environment.usageTracking.enabled is false.
 */
@Injectable({
  providedIn: 'root'
})
export class UsageTrackingService implements OnDestroy {

  /** Per-tab for now — per-tab vs per-browser is an open item on the spec. */
  readonly sessionId = generateUuid();

  /**
   * Route pattern of the page currently in view — set by the host app's page
   * tracking, stamped as X-Route-Pattern on API requests so server-captured
   * api events join the page funnel.
   */
  currentRoutePattern?: string;

  private readonly config: UsageTrackingConfig = environment.usageTracking;
  private readonly endpoint = `${environment.apiEndpoint}/usage/events`;
  private pending: UsageEvent[] = [];
  private droppedCount = 0;
  private started = false;
  private flushInFlight = false;
  private flushTimerId?: ReturnType<typeof setInterval>;
  private persistTimerId?: ReturnType<typeof setTimeout>;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private zone: NgZone
  ) { }

  /**
   * Starts the service: replays events persisted by a previous session
   * (backfill), then arms the flush timer and unload listeners. Called once
   * by the host app on startup; safe to call again (no-op).
   */
  init(): void {
    if (!this.config.enabled || this.started) return;
    this.started = true;

    this.restorePersistedQueue();

    // The session is per-tab (sessionId is minted per service instance), so it
    // starts at app load — login is an auth event within it, not a new session.
    this.capture('session', 'session-start', { authenticated: this.auth.getUser() != null });

    // Timer + DOM listeners run outside Angular so a background flush never
    // triggers change detection.
    this.zone.runOutsideAngular(() => {
      this.flushTimerId = setInterval(() => {
        void this.flush();
      }, this.config.flushIntervalSeconds * 1000);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('focus', this.onWindowFocus);
      window.addEventListener('blur', this.onWindowBlur);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      // Capture phase so clicks are seen even when a handler stops propagation.
      document.addEventListener('click', this.onDocumentClick, true);
    });

    if (this.pending.length > 0) {
      void this.flush();
    }
  }

  /** Public API for explicit domain events. */
  track(name: string, props?: Record<string, unknown>): void {
    this.capture('custom', name, props);
  }

  /**
   * Forces an immediate flush. Callers await this before an auth transition
   * (logout, impersonation) so buffered events send under the JWT of the user
   * who generated them — the API stamps identity from the flushing token.
   */
  flushNow(): Promise<void> {
    return this.flush();
  }

  /**
   * Captures one event into the buffer. Used by the tier wiring (page,
   * session/auth, interaction); `overrides` lets callers set envelope fields
   * such as routePattern.
   */
  capture(type: UsageEventType, name: string, props?: Record<string, unknown>, overrides?: Partial<UsageEvent>): void {
    if (!this.config.enabled) return;

    const user = this.auth.getUser();
    const event: UsageEvent = {
      eventId: generateUuid(),
      sessionId: this.sessionId,
      userId: user?.id,
      instanceId: user?.instanceId,
      timestamp: new Date().toISOString(),
      type: type,
      name: name,
      url: window.location.href,
      // Every event carries the current page's pattern so interactions and
      // session events are page-scopable in reporting; overrides still win.
      routePattern: this.currentRoutePattern,
      props: props,
      ...overrides
    };

    this.pending.push(event);
    if (this.pending.length > this.config.storageQueueLimit) {
      const overflow = this.pending.length - this.config.storageQueueLimit;
      this.pending.splice(0, overflow);
      this.droppedCount += overflow;
    }
    this.schedulePersist();

    if (this.pending.length >= this.config.flushMaxEvents) {
      void this.flush();
    }
  }

  ngOnDestroy(): void {
    if (this.flushTimerId) clearInterval(this.flushTimerId);
    if (this.persistTimerId) clearTimeout(this.persistTimerId);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('click', this.onDocumentClick, true);
    this.persistNow();
  }

  /**
   * Sends buffered events in postBatchLimit chunks. Events stay in the
   * buffer (and localStorage) until their POST succeeds, so a failed or
   * interrupted flush is retried later — duplicates are possible and are
   * deduped downstream by eventId.
   */
  private async flush(useKeepalive = false): Promise<void> {
    if (this.pending.length === 0) return;
    // pagehide: the browser aborts any in-flight HttpClient POST at unload, so
    // the keepalive flush must not be blocked by it; overlap dedupes by eventId.
    if (this.flushInFlight && !useKeepalive) return;
    this.flushInFlight = true;
    try {
      do {
        const droppedSnapshot = this.droppedCount;
        const sliceLimit = droppedSnapshot > 0 ? this.config.postBatchLimit - 1 : this.config.postBatchLimit;
        const batch = this.pending.slice(0, sliceLimit);
        const payload = droppedSnapshot > 0
          ? [this.buildOverflowEvent(), ...batch]
          : batch;
        try {
          await this.post(payload, useKeepalive);
        } catch (error) {
          // A non-auth 4xx means the payload itself was rejected — retrying the
          // same batch would jam the queue forever, so drop it and record the
          // loss. Anything else (network, 5xx, 401/403) stays queued for retry.
          const status = (error as { status?: number })?.status ?? 0;
          if (status >= 400 && status < 500 && status !== 401 && status !== 403) {
            this.removeFromPending(batch);
            this.droppedCount += batch.length;
          }
          return;
        }
        this.droppedCount -= droppedSnapshot;
        this.removeFromPending(batch);
      } while (this.pending.length >= this.config.flushMaxEvents);
    } finally {
      this.flushInFlight = false;
      this.persistNow();
    }
  }

  /** Removal is by eventId, not position — capture()'s overflow trim can shift the queue head while a POST is awaited. */
  private removeFromPending(sent: UsageEvent[]): void {
    const sentIds = new Set(sent.map(sentEvent => sentEvent.eventId));
    this.pending = this.pending.filter(pendingEvent => !sentIds.has(pendingEvent.eventId));
  }

  private async post(events: UsageEvent[], useKeepalive: boolean): Promise<void> {
    if (useKeepalive) {
      // Unload path: HttpClient requests are dropped on page close, so use
      // fetch keepalive (~64KB cap — best effort; localStorage covers the rest).
      const response = await fetch(this.endpoint, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.auth.getToken()}`
        },
        body: JSON.stringify(events)
      });
      if (!response.ok) throw new Error(`Usage flush failed: ${response.status}`);
    } else {
      await firstValueFrom(this.http.post<void>(this.endpoint, events));
    }
  }

  private buildOverflowEvent(): UsageEvent {
    return {
      eventId: generateUuid(),
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      type: 'custom',
      name: 'usage-queue-overflow',
      props: { droppedCount: this.droppedCount }
    };
  }

  private readonly onPageHide = (): void => {
    this.persistNow();
    void this.flush(true);
  };

  private readonly onDocumentClick = (event: MouseEvent): void => {
    try {
      // Synthetic .click() calls from libraries aren't user interactions.
      if (!event.isTrusted) return;
      const target = resolveInteractionTarget(event.target);
      if (!target) return;
      const descriptor = buildInteractionDescriptor(target);
      this.capture('interaction', descriptor.name, descriptor.props);
    } catch {
      // Interaction capture must never break the click it observes.
    }
  };

  private readonly onWindowFocus = (): void => {
    this.capture('session', 'tab-focus');
  };

  private readonly onWindowBlur = (): void => {
    this.capture('session', 'tab-blur');
  };

  private readonly onVisibilityChange = (): void => {
    // Capture before persisting so the visibility event itself is persisted.
    this.capture('session', document.visibilityState === 'hidden' ? 'tab-hidden' : 'tab-visible');
    if (document.visibilityState === 'hidden') this.persistNow();
  };

  private schedulePersist(): void {
    if (this.persistTimerId) return;
    this.persistTimerId = setTimeout(() => {
      this.persistTimerId = undefined;
      this.persistNow();
    }, PERSIST_DEBOUNCE_MS);
  }

  private persistNow(): void {
    try {
      const stored: StoredQueue = { events: this.pending, droppedCount: this.droppedCount };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Quota exceeded — buffered events survive in memory for this session.
    }
  }

  private restorePersistedQueue(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const stored: StoredQueue = JSON.parse(raw);
      // The API restamps identity from the flushing JWT, so another user's
      // leftover events must not be flushed under this login — drop them
      // (recorded in the dropped count) instead of misattributing them.
      const currentUserId = this.auth.getUser()?.id;
      const storedEvents = stored.events ?? [];
      const restorable = storedEvents.filter(storedEvent => !storedEvent.userId || storedEvent.userId === currentUserId);
      // Backfilled events keep their original timestamp + sessionId, so late
      // delivery is still attributed to the session that captured them.
      this.pending = [...restorable, ...this.pending];
      this.droppedCount += (stored.droppedCount ?? 0) + (storedEvents.length - restorable.length);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
}
